// src/econ/params.ts — every econ coefficient, each tagged with provenance.
//
// These are CALIBRATED to three published reference points (not estimated from
// micro-data), so they are ESTIMATE: editable in the assumptions panel, where an
// override flips the field to USER. Mirrors model/seed.ts's COEF discipline.
// See docs/ECON_MODEL_SPEC.md for the role of each.

import type { Flag } from "./types.ts";

export interface EconCoef {
  v: number;
  lab: string;
  hint: string;
  flag: Flag;
}

export interface EconParams {
  gdpScale: EconCoef; // G_a
  gdpExp: EconCoef; // G_b
  shiftScale: EconCoef; // S_a
  shiftExp: EconCoef; // S_b
  shiftCap: EconCoef; // S_cap
  wageAug: EconCoef; // W_aug
  wageAuto: EconCoef; // W_auto
  wageSpill: EconCoef; // W_spill
  permOut: EconCoef; // P_perm
}

const c = (v: number, lab: string, hint: string): EconCoef => ({ v, lab, hint, flag: "ESTIMATE" });

/** Calibrated coefficients. See docs/ECON_MODEL_SPEC.md §3–§6. */
export const ECON_PARAMS: EconParams = {
  gdpScale: c(0.275, "GDP response scale", "Scale of the concave GDP response to AI-effect intensity X = ks·κ·α·(π−1). Calibrated to the three reference GDP uplifts."),
  gdpExp: c(0.59, "GDP response exponent", "Diminishing returns on task automation. <1 means each extra unit of automation adds less GDP than the last."),
  shiftScale: c(0.756, "Labor→capital shift scale", "How strongly automation penetration A = ks·κ·α·aut shifts income from labor to capital."),
  shiftExp: c(0.807, "Shift exponent", "Diminishing returns on the factor-share shift."),
  shiftCap: c(0.9, "Max share shift", "Ceiling on the fractional fall in labor share, so extremes stay sane (INV-E6)."),
  wageAug: c(0.30, "Augmentation wage gain", "How much augmented knowledge tasks lift knowledge wages per unit of (π−1)."),
  wageAuto: c(0.25, "Automation wage pressure", "How much automated knowledge tasks depress knowledge wages."),
  wageSpill: c(0.50, "Spillover to other workers", "Share of GDP uplift that flows to non-knowledge workers' wages (complementarity)."),
  permOut: c(0.30, "Permanently displaced share", "Of workers who don't reallocate within the horizon, the share who leave the workforce rather than remain in transition."),
};

/** Fresh deep copy so the UI's editable panel never mutates the shared default. */
export function baseEconParams(): EconParams {
  return structuredClone(ECON_PARAMS);
}
